import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { monthNames } from "../../../util/rekapAbsensiUtils";

// Teks periode seperti yang tampil di layar ("13 Okt 2026") + format ISO,
// supaya pencarian "okt" maupun "2026-10-13" sama-sama ketemu.
const periodeSearchText = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d)) return `${dateStr}`;
  const tampil = `${String(d.getDate()).padStart(2, "0")} ${monthNames[
    d.getMonth()
  ].slice(0, 3)} ${d.getFullYear()}`;
  return `${dateStr} ${tampil}`;
};

export const usePayrollHistory = () => {
  const getToken = localStorage.getItem("auth") || null;
  const getUser = localStorage.getItem("user");
  const jsonUser = getUser ? JSON.parse(getUser) : {};

  const [historyList, setHistoryList] = useState([]);
  const [detailCache, setDetailCache] = useState({}); // { [batchId]: batch }
  const [detailLoading, setDetailLoading] = useState(null); // batchId
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // filter di sini berbasis TANGGAL RESET (kapan payroll diproses/dibayar),
  // bukan periode absensi yang direkap — lihat catatan di backend
  const [resetDateFrom, setResetDateFrom] = useState("");
  const [resetDateTo, setResetDateTo] = useState("");

  const [typeKaryawan, setTypeKaryawan] = useState("");
  const [valueSearch, setValueSearch] = useState("");
  const [batalkanTarget, setBatalkanTarget] = useState(null);
  const [batalkanLoading, setBatalkanLoading] = useState(false);

  const headers = { Authorization: `Bearer ${getToken}` };

  const getHistory = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_ENDPOINT}/getPayrollHistory`,
        {
          tim: jsonUser.tim,
          resetDateFrom: resetDateFrom || null,
          resetDateTo: resetDateTo || null,
          typeKaryawan: typeKaryawan || null,
        },
        { headers }
      );

      setHistoryList(response.data.data || []);
    } catch (error) {
      setErrorMessage("Gagal mengambil riwayat payroll");
      setHistoryList([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetDateFrom, resetDateTo, typeKaryawan, getToken]);

  useEffect(() => {
    getHistory();
  }, [getHistory]);

  const getDetail = useCallback(
    async (batchId) => {
      if (detailCache[batchId]) return detailCache[batchId];

      setDetailLoading(batchId);
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_ENDPOINT}/getPayrollHistoryDetail`,
          { batchId },
          { headers }
        );

        const batch = response.data.data;
        setDetailCache((prev) => ({ ...prev, [batchId]: batch }));
        return batch;
      } catch (error) {
        setErrorMessage("Gagal mengambil detail payroll");
        return null;
      } finally {
        setDetailLoading(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detailCache, getToken]
  );

  const handleBatalkanPayroll = useCallback(async () => {
    if (!batalkanTarget) return;

    setBatalkanLoading(true);
    setErrorMessage("");

    try {
      await axios.post(
        `${process.env.REACT_APP_ENDPOINT}/batalkanPayroll`,
        { batchId: batalkanTarget._id },
        { headers }
      );

      setDetailCache((prev) => {
        const next = { ...prev };
        delete next[batalkanTarget._id];
        return next;
      });
      setBatalkanTarget(null);
      await getHistory();
    } catch (error) {
      setErrorMessage("Gagal membatalkan payroll");
    } finally {
      setBatalkanLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batalkanTarget, getHistory, getToken]);

  // pencarian periode / catatan / pemroses dilakukan di client dari daftar batch
  const displayedHistory = historyList.filter((batch) => {
    if (!valueSearch) return true;
    const keyword = valueSearch.toLowerCase();
    const haystack = [
      periodeSearchText(batch.periodeStart),
      periodeSearchText(batch.periodeEnd),
      batch.catatan || "",
      batch.resetBy || "",
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(keyword);
  });

  return {
    jsonUser,
    historyList: displayedHistory,
    detailCache,
    detailLoading,
    getDetail,
    loading,
    errorMessage,
    resetDateFrom,
    setResetDateFrom,
    resetDateTo,
    setResetDateTo,
    typeKaryawan,
    setTypeKaryawan,
    valueSearch,
    setValueSearch,
    batalkanTarget,
    setBatalkanTarget,
    batalkanLoading,
    handleBatalkanPayroll,
    refetch: getHistory,
  };
};
